import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchProducts, deleteProduct } from '../services/productService';
import { Product, SortConfig } from '../utils/productUtils';
import { applyListMutations } from '../utils/listMutationLog';
import { useListMutationLog } from './useListMutationLog';

/** Identitaet eines Produkts. Modulebene ⇒ stabile Referenz fuer Hook-Dependencies. */
const getProductId = (product: Product): string => product.id;

export const useProducts = () => {
  const { currentUser, effectiveScopeUid } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  
  // ── Nebenlaeufigkeit: Generationen-Zaehler + Mutations-Log ────────────────
  //
  // `loadGenerationRef` zaehlt bei jedem `loadProducts`-Start hoch (Muster wie
  // InboxPage.tsx): eine UEBERHOLTE Antwort darf weder `products` noch
  // `loading` noch `error` schreiben. Sie wird verworfen, nicht gemergt — die
  // neuere Antwort ist in jeder Hinsicht besser informiert.
  //
  // Das Mutations-Log ersetzt den frueher hier stehenden zweiten Zaehler
  // (`mutationGenerationRef`). Der verwarf die KOMPLETTE Serverantwort, sobald
  // seit dem Anfragestart lokal mutiert worden war. Bei der Erstladung hiess
  // das: nur das gerade lokal angelegte Produkt bleibt uebrig — der
  // `ProductSearchHeader` mit "Neues Produkt" liegt ausserhalb des
  // `loading`-Zweigs, dieser Pfad ist also waehrend der Erstladung erreichbar
  // (gleiche Exposition wie die Kundenliste).
  //
  // Stattdessen gilt jetzt: Serverantwort = Basis, das seit Ladungsstart
  // protokollierte lokale Delta wird darueber gelegt (`applyListMutations`).
  // Spezifikation: `architecture-list-concurrency-merge.md` §3.
  const loadGenerationRef = useRef(0);
  const productLog = useListMutationLog<Product>(getProductId);

  // Lade Produkte aus der Firestore-Datenbank
  const loadProducts = useCallback(async () => {
    if (!currentUser) return;

    const myGen = ++loadGenerationRef.current;
    /** Startmarke im Mutations-Log: alles danach kennt dieser Snapshot nicht. */
    const myMark = productLog.mark();
    /** Eine neuere Anfrage laeuft — deren Antwort gewinnt. */
    const isSuperseded = () => myGen !== loadGenerationRef.current;

    setLoading(true);
    setError(null);

    try {
      const fetchedProducts = await fetchProducts(effectiveScopeUid);
      if (isSuperseded()) return;

      if (productLog.isOverflowed()) {
        // Fallback auf das historische Verhalten (Antwort verwerfen) — per
        // Definition keine Regression, praktisch unerreichbar.
        productLog.settleUpTo(myMark);
        return;
      }

      // Serverantwort = Basis, lokales Delta = Overlay.
      setProducts(applyListMutations(fetchedProducts, productLog.pendingSince(myMark), getProductId));
      // Diese Ladung ist die juengste (sonst waere sie ueberholt) und hat
      // gerade geschrieben: alle Log-Eintraege stecken im neuen State.
      productLog.settleApplied();
    } catch (err: any) {
      if (isSuperseded()) return;
      console.error('Fehler beim Laden der Produkte:', err);
      setError(`loadError:${err.message || 'unknownError'}`);
      // Die Liste bleibt unangetastet; Eintraege bis zur Startmarke sind in
      // jeder kuenftigen Serverantwort enthalten.
      productLog.settleUpTo(myMark);
    } finally {
      // Nur die juengste Anfrage besitzt den Lade-Zustand. Jede nicht
      // ueberholte Antwort loest ihn auf — auch die, die im Overflow-Fallback
      // nichts geschrieben hat, sonst bliebe der Spinner stehen.
      if (!isSuperseded()) setLoading(false);
    }
  }, [currentUser, effectiveScopeUid, productLog]);
  
  // Lade Produkte beim ersten Rendern
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);
  
  // Sortierung
  const requestSort = useCallback((key: keyof Product) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);
  
  // Filtern und Sortieren der Produkte
  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];
    
    // Filtern nach Suchbegriff
    if (searchTerm) {
      result = result.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.sellerArticleId && product.sellerArticleId.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Sortieren
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof Product];
        const bValue = b[sortConfig.key as keyof Product];
        
        if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
        if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Für numerische Werte
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        return 0;
      });
    }
    
    return result;
  }, [products, searchTerm, sortConfig]);
  
  // Produkt löschen
  const handleDeleteProduct = useCallback(async (productId: string) => {
    if (!currentUser) return;
    
    try {
      await deleteProduct(effectiveScopeUid, productId);
      // Protokollieren erst NACH der Server-Bestaetigung (ein fehlgeschlagener
      // Schreibvorgang darf keinen Eintrag hinterlassen) und ausserhalb des
      // `setState`-Updaters (StrictMode ruft Updater doppelt auf).
      productLog.recordDelete([productId]);
      setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
      return true;
    } catch (err: any) {
      console.error('Fehler beim Löschen des Produkts:', err);
      setError(`deleteError:${err.message || 'unknownError'}`);
      return false;
    }
  }, [currentUser, effectiveScopeUid, productLog]);
  
  /**
   * Uebernimmt ein serverseitig bereits gespeichertes Produkt optimistisch in
   * die lokale Liste.
   *
   * @param updatedProduct Der gespeicherte Datensatz (nach dem `await`).
   * @param kind `'create'` (Default) = insert-or-replace beim Replay einer
   *   nebenlaeufigen Ladeantwort; `'update'` = patch-if-present, d.h. enthaelt
   *   die Serverantwort das Produkt nicht mehr, hat es ein anderes Geraet
   *   geloescht und es darf nicht wiederauferstehen. Das Argument ist optional,
   *   damit bestehende Aufrufer unveraendert kompilieren.
   */
  const updateProductInList = useCallback((updatedProduct: Product, kind: 'create' | 'update' = 'create') => {
    // Protokollieren ausserhalb des `setState`-Updaters (StrictMode ruft
    // Updater doppelt auf). Der Aufrufer ruft diese Funktion erst nach der
    // Server-Bestaetigung des Speicherns auf.
    if (kind === 'update') {
      productLog.recordUpdate([updatedProduct]);
    } else {
      productLog.recordCreate([updatedProduct]);
    }
    setProducts(prevProducts => {
      const existingIndex = prevProducts.findIndex(p => p.id === updatedProduct.id);
      if (existingIndex >= 0) {
        // Aktualisiere vorhandenes Produkt
        const newProducts = [...prevProducts];
        newProducts[existingIndex] = updatedProduct;
        return newProducts;
      } else {
        // Füge neues Produkt hinzu
        return [...prevProducts, updatedProduct];
      }
    });
  }, [productLog]);
  
  // Hilfsfunktion für Sortierindikator
  const getSortIndicator = useCallback((key: keyof Product): string => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  }, [sortConfig]);
  
  return {
    products,
    loading,
    error,
    setError,
    searchTerm,
    setSearchTerm,
    sortConfig,
    requestSort,
    filteredAndSortedProducts,
    handleDeleteProduct,
    updateProductInList,
    loadProducts,
    getSortIndicator
  };
}; 